import {
	Bell,
	Building2,
	Check,
	FileText,
	Landmark,
	Loader2,
	MapPin,
	Search,
	Shield,
	TrendingUp,
	Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { WebSiteJsonLd } from "~/components/seo/website-jsonld";
import { companySlug } from "~/lib/company-profile";
import { useAuth } from "~/lib/hooks/use-auth";
import { useCompanyAutocomplete } from "~/lib/hooks/use-company-autocomplete";
import { generateMetaTags, SITE_URL } from "~/lib/seo";

export function meta() {
	return generateMetaTags({
		title: "FirmIndex – Finanční data českých firem na jednom místě",
		description:
			"Vyhledávejte české firmy podle názvu nebo IČO. Obchodní rejstřík, finanční údaje, statutární orgány a další informace na jednom místě.",
		canonical: SITE_URL,
	});
}

export default function Home() {
	return (
		<>
			<WebSiteJsonLd />
			<HeroSection />
			<FeaturesSection />
			<PricingPreview />
			<CTASection />
		</>
	);
}

/* ────────────────────────────── Hero ────────────────────────────── */

function HeroSection() {
	const navigate = useNavigate();
	const [query, setQuery] = useState("");
	const [showDropdown, setShowDropdown] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const { suggestions, status: autocompleteStatus, error } = useCompanyAutocomplete(query);

	const handleInputChange = (value: string) => {
		setQuery(value);
		setShowDropdown(false);
		setActiveIndex(-1);
	};

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setShowDropdown(false);
		if (query.trim()) {
			navigate(`/search?q=${encodeURIComponent(query.trim())}`);
		}
	};

	const selectSuggestion = (suggestion: (typeof suggestions)[number]) => {
		setShowDropdown(false);
		navigate(`/firma/${suggestion.ico}-${companySlug(suggestion.name)}`);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!showDropdown || suggestions.length === 0) return;

		if (e.key === "ArrowDown") {
			e.preventDefault();
			setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
		} else if (e.key === "Enter" && activeIndex >= 0) {
			e.preventDefault();
			selectSuggestion(suggestions[activeIndex]);
		} else if (e.key === "Escape") {
			setShowDropdown(false);
		}
	};

	useEffect(() => {
		if (autocompleteStatus !== "success") return;
		setShowDropdown(suggestions.length > 0);
		setActiveIndex(-1);
	}, [autocompleteStatus, suggestions]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node) &&
				inputRef.current &&
				!inputRef.current.contains(e.target as Node)
			) {
				setShowDropdown(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	return (
		<section className="relative bg-gradient-to-b from-primary-50 to-background px-6 pb-20 pt-16 lg:px-8 lg:pb-28 lg:pt-24">
			<div className="mx-auto max-w-4xl text-center">
				<h1 className="text-4xl font-extrabold tracking-tight text-text sm:text-5xl lg:text-6xl">
					Finanční data o každé české firmě{" "}
					<span className="text-primary-600">na jednom místě</span>
				</h1>
				<p className="mx-auto mt-6 max-w-2xl text-lg text-muted lg:text-xl">
					Získejte přístup k účetním závěrkám, finančním ukazatelům, údajům z obchodního rejstříku a
					dalším datům — vše přehledně a aktuálně.
				</p>

				{/* Functional autocomplete search */}
				<div className="relative mx-auto mt-10 max-w-2xl">
					<form onSubmit={handleSearch} className="flex items-center gap-2">
						<div className="relative flex-1">
							<Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
							<input
								ref={inputRef}
								type="text"
								value={query}
								onChange={(e) => handleInputChange(e.target.value)}
								onFocus={() => {
									if (suggestions.length > 0) setShowDropdown(true);
								}}
								onKeyDown={handleKeyDown}
								placeholder="Hledejte firmu podle názvu nebo IČO..."
								className="w-full rounded-full border border-transparent bg-white py-4 pl-12 pr-12 text-base shadow-card outline-none transition-all focus:border-primary-300 focus:ring-4 focus:ring-primary-100 placeholder:text-gray-400"
								autoComplete="off"
								role="combobox"
								aria-label="Název firmy nebo IČO"
								aria-autocomplete="list"
								aria-expanded={showDropdown && suggestions.length > 0}
								aria-controls="hero-search-listbox"
								aria-describedby={error ? "hero-search-error" : undefined}
								aria-activedescendant={
									activeIndex >= 0 && suggestions[activeIndex]
										? `hero-search-option-${suggestions[activeIndex].ico}`
										: undefined
								}
							/>
							{autocompleteStatus === "loading" && (
								<Loader2
									className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-primary-600"
									aria-label="Načítám návrhy"
								/>
							)}
						</div>
						<button
							type="submit"
							className="btn btn-primary h-12 rounded-full px-6 text-white"
							disabled={query.trim().length < 2}
						>
							Hledat
						</button>
					</form>

					{autocompleteStatus === "error" && error && (
						<p
							id="hero-search-error"
							role="alert"
							className="mt-2 rounded-xl bg-error/10 px-4 py-2 text-left text-sm text-error"
						>
							{error}
						</p>
					)}

					{showDropdown && suggestions.length > 0 && (
						<div
							ref={dropdownRef}
							id="hero-search-listbox"
							role="listbox"
							className="absolute left-0 right-0 z-10 mt-2 max-h-96 overflow-y-auto rounded-2xl bg-white py-2 shadow-pop"
						>
							{suggestions.map((suggestion, index) => (
								<button
									key={suggestion.ico}
									id={`hero-search-option-${suggestion.ico}`}
									type="button"
									role="option"
									aria-selected={index === activeIndex}
									className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors ${
										index === activeIndex ? "bg-primary-50" : "hover:bg-primary-50"
									}`}
									onMouseDown={() => selectSuggestion(suggestion)}
									onMouseEnter={() => setActiveIndex(index)}
								>
									<div>
										<p className="text-sm font-medium text-text">{suggestion.name}</p>
										<p className="text-xs text-muted">
											IČO: <span className="font-mono">{suggestion.ico}</span>
											{suggestion.city && (
												<>
													{" "}
													<MapPin className="inline h-3 w-3" /> {suggestion.city}
												</>
											)}
										</p>
									</div>
								</button>
							))}
						</div>
					)}
				</div>

				<div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm font-medium text-muted">
					<span className="flex items-center gap-2">
						<Building2 size={16} className="text-primary-500" />
						500 000+ firem
					</span>
					<span className="flex items-center gap-2">
						<TrendingUp size={16} className="text-primary-500" />5 let historie
					</span>
					<span className="flex items-center gap-2">
						<Shield size={16} className="text-primary-500" />
						Průběžně ověřované zdroje
					</span>
				</div>
			</div>
		</section>
	);
}

/* ────────────────────────────── Features ────────────────────────── */

const freeFeatures = [
	{
		icon: Search,
		title: "Vyhledávání firem",
		description: "Rychlé vyhledávání podle názvu, IČO nebo jména jednatele.",
	},
	{
		icon: Building2,
		title: "Registrační data",
		description: "Sídlo, datum založení, právní forma a další základní údaje.",
	},
	{
		icon: Users,
		title: "Jednatelé a společníci",
		description: "Aktuální i historický přehled statutárních orgánů firmy.",
	},
];

const premiumFeatures = [
	{
		icon: FileText,
		title: "Účetní závěrky",
		description: "Rozvaha, výsledovka a přílohy za posledních 5 let v přehledných grafech.",
	},
	{
		icon: TrendingUp,
		title: "Finanční ukazatele",
		description: "Likvidita, ziskovost, zadluženost a další klíčové ukazatele s YoY změnami.",
	},
	{
		icon: Bell,
		title: "Monitoring firem",
		description: "Automatická upozornění na změny v rejstříku, insolvence a exekuce.",
	},
	{
		icon: Landmark,
		title: "Firemní události z listin",
		description:
			"Fúze, změny kapitálu a vztahy ve skupině extrahované z notářských zápisů a dalších listin — týdny před zápisem do rejstříku.",
	},
];

function FeaturesSection() {
	return (
		<section id="funkce" className="bg-surface px-6 py-20 lg:px-8 lg:py-28">
			<div className="mx-auto max-w-7xl">
				<div className="text-center">
					<h2 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">
						Vše, co potřebujete vědět o české firmě
					</h2>
					<p className="mx-auto mt-4 max-w-2xl text-muted">
						Od základních registračních dat až po detailní finanční analýzu — vyberte si plán, který
						vám vyhovuje.
					</p>
				</div>

				{/* Free features */}
				<div className="mt-16">
					<div className="mb-8 flex items-center gap-3">
						<span className="rounded-full bg-secondary-100 px-4 py-1 text-sm font-semibold text-secondary-700">
							Zdarma
						</span>
						<span className="text-sm text-muted">Základní data bez registrace</span>
					</div>
					<div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
						{freeFeatures.map((f) => (
							<FeatureCard key={f.title} {...f} />
						))}
					</div>
				</div>

				{/* Premium features */}
				<div className="mt-16">
					<div className="mb-8 flex items-center gap-3">
						<span className="rounded-full bg-primary-100 px-4 py-1 text-sm font-semibold text-primary-700">
							Premium
						</span>
						<span className="text-sm text-muted">Pro profesionály a firmy</span>
					</div>
					<div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
						{premiumFeatures.map((f) => (
							<FeatureCard key={f.title} {...f} />
						))}
					</div>
				</div>
			</div>
		</section>
	);
}

function FeatureCard({
	icon: Icon,
	title,
	description,
}: {
	icon: typeof Search;
	title: string;
	description: string;
}) {
	return (
		<div className="rounded-2xl bg-white p-6 shadow-card transition-shadow hover:shadow-md">
			<div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50">
				<Icon size={20} className="text-primary-600" />
			</div>
			<h3 className="text-lg font-semibold text-text">{title}</h3>
			<p className="mt-2 text-sm text-muted">{description}</p>
		</div>
	);
}

/* ────────────────────────────── Pricing Preview ─────────────────── */

const tiers = [
	{
		name: "Zdarma",
		price: "0 Kč",
		annualPrice: "0 Kč",
		period: "",
		description: "Základní přehled pro každého",
		features: [
			"Vyhledávání firem",
			"3 roky finančních výsledků",
			"Monitoring 5 firem",
			"E-mailová upozornění",
		],
		cta: "Začít zdarma",
		highlighted: false,
	},
	{
		name: "Premium",
		price: "699 Kč",
		annualPrice: "583 Kč",
		period: "/měs",
		description: "Pro podnikatele a analytiky",
		features: [
			"Vše ze Zdarma",
			"5 let finančních výsledků",
			"Plné FirmIndex skóre",
			"Monitoring 100 firem",
			"PDF a Excel export",
			"API přístup",
		],
		cta: "Od podzimu 2026",
		highlighted: true,
	},
	{
		name: "Pro",
		price: "1 499 Kč",
		annualPrice: "1 249 Kč",
		period: "/měs",
		description: "Pro týmy a větší firmy",
		features: [
			"Vše z Premium",
			"Srovnání s odvětvím",
			"Monitoring 1 000 firem",
			"Hromadné exporty",
			"Prioritní podpora",
		],
		cta: "Od podzimu 2026",
		highlighted: false,
	},
	{
		name: "Enterprise",
		price: "Na míru",
		annualPrice: "Na míru",
		period: "",
		description: "Pro velké organizace",
		features: [
			"Vše z Pro",
			"Neomezený monitoring",
			"Vlastní integrace",
			"SLA garance",
			"Dedikovaná podpora",
		],
		cta: "Kontaktujte nás",
		highlighted: false,
	},
];

function PricingPreview() {
	const [annual, setAnnual] = useState(false);

	return (
		<section className="bg-background px-6 py-20 lg:px-8 lg:py-28">
			<div className="mx-auto max-w-7xl">
				<div className="text-center">
					<h2 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">
						Jednoduchý a transparentní ceník
					</h2>
					<p className="mx-auto mt-4 max-w-2xl text-muted">
						Bezplatný účet můžete využívat už dnes. Placené plány připravujeme na podzim 2026.
					</p>

					{/* Toggle */}
					<div className="mt-8 inline-flex items-center gap-1 rounded-full bg-white p-1 shadow-card">
						<button
							type="button"
							onClick={() => setAnnual(false)}
							className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
								!annual ? "bg-primary-500 text-white" : "text-muted hover:text-text"
							}`}
						>
							Měsíčně
						</button>
						<button
							type="button"
							onClick={() => setAnnual(true)}
							className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
								annual ? "bg-primary-500 text-white" : "text-muted hover:text-text"
							}`}
						>
							Ročně
							<span
								className={`ml-1.5 text-xs font-semibold ${
									annual ? "text-white/90" : "text-secondary-500"
								}`}
							>
								-2 měsíce
							</span>
						</button>
					</div>
				</div>

				<div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
					{tiers.map((tier) => (
						<div
							key={tier.name}
							className={`relative rounded-2xl p-6 ${
								tier.highlighted
									? "bg-white shadow-lg ring-2 ring-primary-500"
									: "bg-white shadow-card"
							}`}
						>
							{tier.highlighted && (
								<div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary-600 px-4 py-1 text-xs font-semibold text-white">
									Nejoblíbenější
								</div>
							)}
							<h3 className="text-lg font-semibold text-text">{tier.name}</h3>
							<p className="mt-1 text-sm text-muted">{tier.description}</p>
							<div className="mt-4">
								<span className="text-3xl font-bold text-text">
									{annual ? tier.annualPrice : tier.price}
								</span>
								{tier.period && <span className="text-muted">{tier.period}</span>}
							</div>
							<ul className="mt-6 flex flex-col gap-3">
								{tier.features.map((f) => (
									<li key={f} className="flex items-start gap-2 text-sm text-muted">
										<Check size={16} className="mt-0.5 shrink-0 text-secondary-500" />
										{f}
									</li>
								))}
							</ul>
							<Link
								to={tier.name === "Enterprise" ? "/kontakt" : "/cenik"}
								className={`mt-8 block rounded-full py-2.5 text-center text-sm font-semibold transition-colors ${
									tier.highlighted
										? "bg-primary-600 text-white hover:bg-primary-700"
										: "bg-gray-100 text-text hover:bg-gray-200"
								}`}
							>
								{tier.cta}
							</Link>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

/* ────────────────────────────── CTA ─────────────────────────────── */

function CTASection() {
	const { user, loading } = useAuth();

	return (
		<section className="bg-gradient-brand px-6 py-20 lg:px-8 lg:py-24">
			<div className="mx-auto max-w-3xl text-center">
				<h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
					Připraveni začít?
				</h2>
				<p className="mt-4 text-lg text-primary-100">
					Vyzkoušejte FirmIndex zdarma a získejte přístup k datům o více než 500 000 českých
					firmách.
				</p>
				<div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
					{!loading &&
						(user ? (
							<Link
								to="/dashboard"
								className="rounded-full bg-white px-8 py-3 text-sm font-semibold text-primary-600 shadow-sm hover:bg-primary-50 transition-colors"
							>
								Přejít na dashboard
							</Link>
						) : (
							<Link
								to="/login"
								className="rounded-full bg-white px-8 py-3 text-sm font-semibold text-primary-600 shadow-sm hover:bg-primary-50 transition-colors"
							>
								Vyzkoušet zdarma
							</Link>
						))}
					<Link
						to="/kontakt"
						className="rounded-full border border-white/40 px-8 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
					>
						Kontaktujte nás
					</Link>
				</div>
			</div>
		</section>
	);
}
